import axios from 'axios';
import { apiClient, getErrorMessage } from '../../utils/apiClient';
import { logger } from '../../utils/logger';
import { toast } from '../../utils/toast';
import {
  API_PACK_ANALYSIS_ASN_INGEST,
  API_PACK_ANALYSIS_ASN_INGEST_CANCEL,
  API_PACK_ANALYSIS_ASN_REJECTED,
  API_PACK_ANALYSIS_ASN_REPROCESS,
  API_PACK_ANALYSIS_ASN_REPROCESS_BATCH,
  API_PACK_ANALYSIS_ASN_LIST,
  API_PACK_ANALYSIS_ASN_CANONICAL,
  API_PACK_ANALYSIS_PACK_MASTER_LIST,
  API_PACK_ANALYSIS_PACK_MASTER_GET,
  API_PACK_ANALYSIS_CARTON_TYPES,
  API_PACK_ANALYSIS_CARTONIZATION,
  API_PACK_ANALYSIS_CARTONIZATION_MLINKONE_BRIDGE,
  API_PACK_ANALYSIS_OBSERVATIONS_SOURCE_ASNS,
  API_EDI_ENROLLMENT_LIST,
  API_EDI_ENROLLMENT_CREATE,
  API_EDI_ENROLLMENT_GET,
  API_EDI_ENROLLMENT_UPDATE,
  API_EDI_ENROLLMENT_DISABLE,
} from '../../constants/apiConstants';

// ============================================================================
// Types — mirror Domain/PackAnalysis/Messages + Domain/Edi/Messages on the server.
// Backend serializes with camelCase, so C# PascalCase ↔ TS camelCase.
// ============================================================================

export interface IngestDiagnostic {
  key: string;
  hierid?: number | null;
  note?: string | null;
}

export interface RejectedAsnItem {
  doc856Id: number;
  fileName?: string | null;
  senderCodeRaw?: string | null;
  receiverCodeRaw?: string | null;
  buyerPartyId?: number | null;
  buyerPartyName?: string | null;
  supplierPartyId?: number | null;
  supplierPartyName?: string | null;
  createdAt?: string | null;
  reprocessAttempts: number;
  primaryReason?: string | null;
}

export interface RejectedAsnListResponse {
  items: RejectedAsnItem[];
  pageIndex: number;
  pageSize: number;
  totalCount: number;
  message?: string;
}

export interface Doc856ReprocessResponse {
  doc856Id: number;
  ingestStatus: string;
  reprocessAttempts: number;
  diagnostics: IngestDiagnostic[];
  message: string;
}

export interface RejectedAsnBatchPerItem {
  doc856Id: number;
  response?: Doc856ReprocessResponse | null;
  statusCode: number;
  statusText: string;
}

export interface RejectedAsnBatchResponse {
  results: RejectedAsnBatchPerItem[];
  succeeded: number;
  stillRejected: number;
  failed: number;
  message: string;
}

export interface AsnIngestPerFile {
  fileName: string;
  response?: {
    doc856Id: number;
    shipmentId?: number | null;
    ingestStatus: string;
    rawRowCount: number;
    canonicalShipmentCount: number;
    canonicalNodeCount: number;
    canonicalHandlingUnitCount: number;
    canonicalShipmentItemCount: number;
    packObservationCount: number;
    diagnostics: IngestDiagnostic[];
    durationMs: number;
    message: string;
  } | null;
  statusCode: number;
  statusText: string;
}

export interface AsnIngestBatchSummary {
  totalFiles: number;
  canonicalized: number;
  canonicalizedWithAnomalies: number;
  duplicates: number;
  parseFailed: number;
  unsupportedFormat: number;
  rejectedNotEnrolled: number;
  serverError: number;
  cancelled: number;
}

export interface CancelIngestResponse {
  clientBatchId: string;
  found: boolean;
}

export interface AsnIngestBatchResponse {
  results: AsnIngestPerFile[];
  summary: AsnIngestBatchSummary;
  message: string;
}

// Enrollment scope options — must match server `PartyAppEnrollment.EnrollmentScopeOptions`.
export const ENROLLMENT_SCOPE = {
  BuyerOnly: 'buyer_only',
  SupplierOnly: 'supplier_only',
  BuyerSupplier: 'buyer_supplier',
} as const;

export type EnrollmentScope = (typeof ENROLLMENT_SCOPE)[keyof typeof ENROLLMENT_SCOPE];

// App code options — keep aligned with server `PartyAppEnrollment.AppCodeOptions`.
export const APP_CODE = {
  PackAnalysis: 'pack-analysis',
  MLinkOne: 'mlinkone',
  SLinkOne: 'slinkone',
} as const;

export type AppCode = (typeof APP_CODE)[keyof typeof APP_CODE];

export interface PartyAppEnrollment {
  enrollmentId: number;
  appCode: string;
  enrollmentScope: string;
  buyerPartyId?: number | null;
  buyerPartyCode?: string | null;
  buyerPartyName?: string | null;
  supplierPartyId?: number | null;
  supplierPartyCode?: string | null;
  supplierPartyName?: string | null;
  enabledAt?: string | null;
  disabledAt?: string | null;
  description?: string | null;
  isActive: boolean;
  status?: string | null;
}

export interface PartyAppEnrollmentListResponse {
  items: PartyAppEnrollment[];
  pageIndex: number;
  pageSize: number;
  totalCount: number;
  message?: string;
}

export interface CreateEnrollmentRequest {
  appCode: string;
  enrollmentScope: string;
  buyerPartyId?: number | null;
  supplierPartyId?: number | null;
  description?: string | null;
}

export interface UpdateEnrollmentRequest {
  description?: string | null;
  isActive?: boolean | null;
}

// ── Observation source-ASN lazy-load (Pack Planner Why Panel) ───────────────
// Spec: openspec/changes/pack-planner-why-panel-source-asns/

export interface ObservationSourceAsnRow {
  asnNum: string;
  doc856Id: number;
  /** ISO-8601 UTC timestamp (BE serializes DateTime as ISO string). */
  mostRecentObservedAt: string;
  observationCount: number;
}

export interface ObservationSourceAsnsResponse {
  asns: ObservationSourceAsnRow[];
  totalCount: number;
  hasMore: boolean;
  message?: string;
}

// ============================================================================
// Service
// ============================================================================

export const packAnalysisService = {
  // ── Rejected ASN list / reprocess ────────────────────────────────────────

  async getRejectedAsns(
    tenantId: number,
    params: {
      buyerCode?: string;
      supplierCode?: string;
      dateFrom?: string;
      dateTo?: string;
      pageIndex?: number;
      pageSize?: number;
    }
  ): Promise<RejectedAsnListResponse> {
    try {
      const response = await apiClient.get(API_PACK_ANALYSIS_ASN_REJECTED, {
        params: {
          tenantId,
          buyerCode: params.buyerCode,
          supplierCode: params.supplierCode,
          dateFrom: params.dateFrom,
          dateTo: params.dateTo,
          pageIndex: params.pageIndex ?? 1,
          pageSize: params.pageSize ?? 20,
        },
      });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data as RejectedAsnListResponse;
    } catch (error) {
      logger.error('Failed to fetch rejected ASNs', error);
      throw error;
    }
  },

  async reprocessAsn(tenantId: number, doc856Id: number): Promise<Doc856ReprocessResponse> {
    try {
      const response = await apiClient.post(API_PACK_ANALYSIS_ASN_REPROCESS(doc856Id), null, {
        params: { tenantId },
        // Reprocess re-runs parse → canonicalize → persist; same shape as ingestAsn.
        // Default 30s timeout is the documented PA trap (feedback_apiclient_default_timeout.md).
        timeout: 600_000,
      });
      // Server may return Succeeded=false when "still rejected" — treat as data,
      // not an exception. Controller always returns HTTP 200 per Result<T> convention.
      return response.data.data as Doc856ReprocessResponse;
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Reprocess failed: ${msg}`);
      throw error;
    }
  },

  async reprocessBatch(tenantId: number, doc856Ids: number[]): Promise<RejectedAsnBatchResponse> {
    try {
      const response = await apiClient.post(
        API_PACK_ANALYSIS_ASN_REPROCESS_BATCH,
        { doc856Ids },
        {
          params: { tenantId },
          // N parallel reprocess pipelines server-side; scale linearly with batch size.
          timeout: 600_000,
        }
      );
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data as RejectedAsnBatchResponse;
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Batch reprocess failed: ${msg}`);
      throw error;
    }
  },

  async ingestAsn(options: {
    tenantId: number;
    files: File[];
    dataSource?: string;
    direction?: string;
    documentVariant?: string;
    buyerCodeHint?: string;
    dryRun?: boolean;
  }): Promise<AsnIngestBatchResponse> {
    try {
      const formData = new FormData();
      for (const f of options.files) formData.append('files', f);
      if (options.dataSource) formData.append('dataSource', options.dataSource);
      if (options.direction) formData.append('direction', options.direction);
      if (options.documentVariant) formData.append('documentVariant', options.documentVariant);
      if (options.buyerCodeHint) formData.append('buyerCodeHint', options.buyerCodeHint);
      if (options.dryRun) formData.append('dryRun', 'true');

      const response = await apiClient.post(API_PACK_ANALYSIS_ASN_INGEST, formData, {
        params: { tenantId: options.tenantId },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data as AsnIngestBatchResponse;
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Ingest failed: ${msg}`);
      throw error;
    }
  },

  // ── Party-App Enrollment CRUD ────────────────────────────────────────────

  async getEnrollments(
    tenantId: number,
    params: {
      appCode?: string;
      buyerPartyId?: number;
      supplierPartyId?: number;
      isActive?: boolean;
      pageIndex?: number;
      pageSize?: number;
    }
  ): Promise<PartyAppEnrollmentListResponse> {
    try {
      const response = await apiClient.get(API_EDI_ENROLLMENT_LIST, {
        params: {
          tenantId,
          appCode: params.appCode,
          buyerPartyId: params.buyerPartyId,
          supplierPartyId: params.supplierPartyId,
          isActive: params.isActive,
          pageIndex: params.pageIndex ?? 1,
          pageSize: params.pageSize ?? 20,
        },
      });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data as PartyAppEnrollmentListResponse;
    } catch (error) {
      logger.error('Failed to fetch enrollments', error);
      throw error;
    }
  },

  async getEnrollmentById(tenantId: number, id: number): Promise<PartyAppEnrollment | null> {
    try {
      const response = await apiClient.get(API_EDI_ENROLLMENT_GET(id), {
        params: { tenantId },
      });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return (response.data.data?.item ?? null) as PartyAppEnrollment | null;
    } catch (error) {
      logger.error('Failed to fetch enrollment', error);
      throw error;
    }
  },

  async createEnrollment(tenantId: number, request: CreateEnrollmentRequest): Promise<{ enrollmentId: number }> {
    try {
      const response = await apiClient.post(API_EDI_ENROLLMENT_CREATE, request, {
        params: { tenantId },
      });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Enrollment created');
      return response.data.data as { enrollmentId: number };
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Create failed: ${msg}`);
      throw error;
    }
  },

  async updateEnrollment(tenantId: number, id: number, request: UpdateEnrollmentRequest): Promise<void> {
    try {
      const response = await apiClient.put(API_EDI_ENROLLMENT_UPDATE(id), request, {
        params: { tenantId },
      });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Enrollment updated');
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Update failed: ${msg}`);
      throw error;
    }
  },

  async disableEnrollment(tenantId: number, id: number): Promise<void> {
    try {
      const response = await apiClient.delete(API_EDI_ENROLLMENT_DISABLE(id), {
        params: { tenantId },
      });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      toast.success('Enrollment disabled');
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Disable failed: ${msg}`);
      throw error;
    }
  },

  // ── Ingest (multipart upload) ────────────────────────────────────────────
  async ingestBatch(
    tenantId: number,
    files: File[],
    options: {
      dataSource?: string;
      direction?: string;
      documentVariant?: string;
      buyerCodeHint?: string;
      dryRun?: boolean;
      clientBatchId?: string;
      onUploadProgress?: (loaded: number, total: number) => void;
      // Aborts the multipart upload locally if the user clicks Cancel before
      // upload-100% — pairs with the server-side cancel POST for after-upload.
      signal?: AbortSignal;
    } = {}
  ): Promise<AsnIngestBatchResponse> {
    try {
      const formData = new FormData();
      for (const file of files) formData.append('files', file, file.name);
      if (options.dataSource) formData.append('dataSource', options.dataSource);
      if (options.direction) formData.append('direction', options.direction);
      if (options.documentVariant) formData.append('documentVariant', options.documentVariant);
      if (options.buyerCodeHint) formData.append('buyerCodeHint', options.buyerCodeHint);
      if (options.dryRun !== undefined) formData.append('dryRun', String(options.dryRun));
      // Correlation id for the SSE progress channel — server publishes events
      // keyed on this id so the subscriber can render live per-file progress.
      if (options.clientBatchId) formData.append('clientBatchId', options.clientBatchId);

      const totalBytes = files.reduce((sum, f) => sum + f.size, 0);

      const response = await apiClient.post(API_PACK_ANALYSIS_ASN_INGEST, formData, {
        params: { tenantId },
        headers: { 'Content-Type': 'multipart/form-data' },
        // ASN ingest runs the full parse → canonicalize → persist pipeline server-side.
        // A single VNV ASN57 file is ~14s warm; cold cache + multi-file batches at parallel=4
        // can cross 60s. 600 MB is the controller-side request cap. 10 minutes covers worst-case
        // without ever timing out the operator on a real submission.
        timeout: 600_000,
        signal: options.signal,
        onUploadProgress: options.onUploadProgress
          ? (e) => {
              // Axios falls back to undefined `total` for chunked uploads; use the FormData
              // payload size we computed instead — that's the only number we can trust.
              options.onUploadProgress!(e.loaded, e.total ?? totalBytes);
            }
          : undefined,
      });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data as AsnIngestBatchResponse;
    } catch (error) {
      // Re-throw cancellation quietly so the thunk maps it to the user-cancel
      // sentinel without lighting up the dev error overlay.
      if (axios.isCancel(error)) throw error;
      logger.error('Ingest batch failed', error);
      throw error;
    }
  },

  // ── Cancel an in-flight ingest batch ─────────────────────────────────────
  // Idempotent — server returns found=false when the id is unknown (already
  // finished, never started, or wrong tab). We swallow that case and treat it
  // as a no-op success on the client.
  async cancelIngestBatch(clientBatchId: string): Promise<CancelIngestResponse> {
    try {
      const response = await apiClient.post(API_PACK_ANALYSIS_ASN_INGEST_CANCEL(clientBatchId));
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data as CancelIngestResponse;
    } catch (error) {
      logger.error('Cancel ingest batch failed', error);
      throw error;
    }
  },

  // ── Workbench: list ASNs (any status) ────────────────────────────────────
  async listAsn(
    tenantId: number,
    params: {
      status?: string;
      buyerCodeHint?: string;
      supplierCodeHint?: string;
      // pack-analysis-edi-module-permissions Slice 2b — active buyer/supplier party
      // ids. Sent so a party-scoped caller is authorized + restricted server-side to
      // the party they hold; ignored for global callers (tenant-wide browse preserved).
      buyerPartyId?: number | null;
      supplierPartyId?: number | null;
      // Pagination-v2 Req 3.1 — ISO 8601 UTC strings; half-open per Req 3.6
      // (BE applies `created_at < dateTo`).
      dateFrom?: string | null;
      dateTo?: string | null;
      pageIndex?: number;
      pageSize?: number;
      // Spec pack-analysis-asn-browser-server-sort. Optional.
      // Sort key matches FE column key (e.g. "asnNum", "cartonCount", "ingestedAt").
      // Direction is "asc" | "desc"; BE defaults to descending order when sortDir
      // is omitted, and falls back to historical CreatedAt DESC when sortBy is omitted.
      sortBy?: string | null;
      sortDir?: 'asc' | 'desc' | null;
    } = {}
  ): Promise<AsnListResponse> {
    try {
      const response = await apiClient.get(API_PACK_ANALYSIS_ASN_LIST, {
        params: {
          tenantId,
          status: params.status,
          buyerCodeHint: params.buyerCodeHint,
          supplierCodeHint: params.supplierCodeHint,
          buyerPartyId: params.buyerPartyId ?? undefined,
          supplierPartyId: params.supplierPartyId ?? undefined,
          dateFrom: params.dateFrom ?? undefined,
          dateTo: params.dateTo ?? undefined,
          pageIndex: params.pageIndex ?? 1,
          pageSize: params.pageSize ?? 50,
          sortBy: params.sortBy ?? undefined,
          sortDir: params.sortDir ?? undefined,
        },
        // Default 30s is risky on first-load after a wipe (cold caches + read-replica lag).
        timeout: 60_000,
      });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data as AsnListResponse;
    } catch (error) {
      logger.error('Failed to list ASNs', error);
      throw error;
    }
  },

  // ── Workbench: ASN canonical drill-down ─────────────────────────────────
  async getAsnCanonical(tenantId: number, doc856Id: number): Promise<AsnCanonicalResponse> {
    try {
      const response = await apiClient.get(API_PACK_ANALYSIS_ASN_CANONICAL(doc856Id), {
        params: { tenantId },
        // Spec pack-analysis-asn-browser-perf-fix Req 4.1 — 60s ceiling.
        // The detail handler runs ~10 sequential repo reads + cross-DB Party
        // lookup; default 30s is too tight on cold cache. Matches the listAsn()
        // override pattern (per feedback_apiclient_default_timeout.md).
        timeout: 60_000,
      });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data as AsnCanonicalResponse;
    } catch (error) {
      logger.error('Failed to fetch ASN canonical drill-down', error);
      throw error;
    }
  },

  // ── Workbench: PackMaster list ──────────────────────────────────────────
  async listPackMasters(
    tenantId: number,
    params: {
      doc856Id?: number;
      compositionSignature?: string;
      mixKind?: string;
      buyerPartyId?: number;
      supplierPartyId?: number;
      pageIndex?: number;
      pageSize?: number;
    }
  ): Promise<PackMasterListResponse> {
    try {
      const response = await apiClient.get(API_PACK_ANALYSIS_PACK_MASTER_LIST, {
        params: {
          tenantId,
          doc856Id: params.doc856Id,
          compositionSignature: params.compositionSignature,
          mixKind: params.mixKind,
          buyerPartyId: params.buyerPartyId,
          supplierPartyId: params.supplierPartyId,
          pageIndex: params.pageIndex ?? 1,
          pageSize: params.pageSize ?? 50,
        },
      });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data as PackMasterListResponse;
    } catch (error) {
      logger.error('Failed to list PackMasters', error);
      throw error;
    }
  },

  // ── Workbench: carton-type list (Bridge Tester carton selector) ─────────
  async listCartonTypes(tenantId: number, signal?: AbortSignal): Promise<CartonTypeListItem[]> {
    try {
      const response = await apiClient.get(API_PACK_ANALYSIS_CARTON_TYPES, {
        params: { tenantId },
        signal,
      });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      const items = response.data.data?.items as CartonTypeListItem[] | undefined;
      return items ?? [];
    } catch (error: unknown) {
      // React 18 StrictMode dev double-mount aborts the first in-flight
      // request via the AbortController the caller passed. Axios surfaces
      // that as CanceledError (`code: 'ERR_CANCELED'`). Callers check
      // `signal.aborted` and ignore — we re-throw without logging so the
      // console isn't polluted with a benign "Failed to list" line on every
      // picker mount.
      const e = error as { name?: string; code?: string };
      const isCancel = e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED';
      if (!isCancel) {
        logger.error('Failed to list carton types', error);
      }
      throw error;
    }
  },

  // ── Workbench: PackMaster single row ────────────────────────────────────
  async getPackMaster(tenantId: number, id: number): Promise<PackMasterRow> {
    try {
      const response = await apiClient.get(API_PACK_ANALYSIS_PACK_MASTER_GET(id), {
        params: { tenantId },
      });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      const item = response.data.data?.item as PackMasterRow | undefined;
      if (!item) throw new Error('PackMaster row not returned by API');
      return item;
    } catch (error) {
      logger.error('Failed to fetch PackMaster', error);
      throw error;
    }
  },

  // ── Pack Planner Why Panel: lazy-load source ASNs ───────────────────────
  // Spec: openspec/changes/pack-planner-why-panel-source-asns/. Joins
  // multiple itemKeys with ',' for Prepack composition support. Accepts
  // either party IDs (preferred) or party codes (BE resolves via the same
  // IPartyCodeResolver the cartonization handler uses, so codes that
  // worked in the cartonization request also work here).
  async getObservationSourceAsns(
    options: {
      tenantId: number;
      itemKeys: string[];
      buyerPartyId?: number | null;
      supplierPartyId?: number | null;
      buyerCode?: string | null;
      supplierCode?: string | null;
      /** When set ("BULK" / "PREPACK" / "FREE_MIX"), restricts trace to
       *  PackMasters of that mix kind. Prevents bulk PackMasters with a
       *  coincidentally-matching sub-itemKey from surfacing on a PREPACK row. */
      mixKind?: string | null;
      limit?: number;
    },
    signal?: AbortSignal,
  ): Promise<ObservationSourceAsnsResponse> {
    try {
      const response = await apiClient.get(API_PACK_ANALYSIS_OBSERVATIONS_SOURCE_ASNS, {
        params: {
          tenantId:        options.tenantId,
          itemKey:         options.itemKeys.join(','),
          buyerPartyId:    options.buyerPartyId ?? undefined,
          supplierPartyId: options.supplierPartyId ?? undefined,
          buyerCode:       options.buyerCode ?? undefined,
          supplierCode:    options.supplierCode ?? undefined,
          mixKind:         options.mixKind ?? undefined,
          limit:           options.limit ?? 25,
        },
        signal,
      });
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', '));
      return response.data.data as ObservationSourceAsnsResponse;
    } catch (error) {
      // No toast — error surfaces inline in the Why Panel per spec
      // ADDED Requirement "Error and Empty Inline Rendering" (D8).
      logger.error('Failed to fetch observation source ASNs', error);
      throw error;
    }
  },

  // ── Workbench: Cartonization simulator ──────────────────────────────────
  async cartonize(
    payload: CartonizationRequest,
  ): Promise<CartonizationResponse | CartonizationMultiShipmentResponse> {
    try {
      const response = await apiClient.post(API_PACK_ANALYSIS_CARTONIZATION, payload);
      const env = response.data;
      // Spec pa-shipment-aware-pack-planning — a request that fans out to MORE THAN ONE shipment
      // returns the canonical-native composite RAW (no `Result<>` envelope): `{ shipments: [...] }`.
      // A single-shipment (or no-`shipmentKey`) request returns the `Result<>` envelope as before.
      if (env && Array.isArray(env.shipments)) {
        return env as CartonizationMultiShipmentResponse;
      }
      // A "couldn't produce a plan" run (`NOT_YET_IMPLEMENTED`) comes back
      // `succeeded:false` but STILL carries a populated `Cartonization_Suggest_Response`
      // in `.data` — empty `cartons`, the failure `message`, and (when
      // `includeEvidence` is set) the full strategy `trace`. Return it so the
      // Simulator can render the "why this plan didn't win" panel + trace
      // instead of just a red alert. Genuine hard errors (unknown unit -4913,
      // hard timeout, unhandled exception) carry no `.data` — those we throw.
      if (env.data) return env.data as CartonizationResponse;
      if (!env.succeeded) throw new Error(env.messages?.join(', ') || 'Cartonization failed');
      return env.data as CartonizationResponse;
    } catch (error) {
      logger.error('Cartonization simulate failed', error);
      throw error;
    }
  },

  /**
   * Spec 3b — POST `/api/v1/pack-analysis/cartonization/mlinkone-bridge`. Used
   * by the MLinkOne Bridge Tester page in the Workbench. Returns the FULL Result
   * envelope (including `succeeded:false` failures like
   * `MLinkOneBridge_ItemKey_NotComposable=-4910`) so the caller can render the
   * status code + messages instead of throwing — testers want to see the
   * envelope shape verbatim, including failures.
   *
   * Cartonization is server-side bounded by a 30s hard timeout but the
   * apiClient default is 30s too. Per `feedback_apiclient_default_timeout.md`
   * we override to 60s to leave room for the BE timeout to surface its own
   * `Timeout=-4902` envelope before axios cancels.
   */
  async cartonizeBridge(
    payload: CartonizationMLinkOneBridgeRequest | CartonizationAutoPackBridgeRequest,
    explain = false
  ): Promise<CartonizationBridgeEnvelope> {
    try {
      // Spec pack-analysis-mlinkone-bridge-autopack-compat — Bridge Tester
      // calls debug=1 so the BE returns the rich envelope with `ml1Shape`
      // populated as a sibling. Without ?debug, the BE returns the slim
      // AutoPack 1.0 wire shape (used by `cartonizeBridgeAutoPack` below).
      // Spec mlinkone-bridge-explain-block — `&explain=1` adds the per-shipment
      // `Explain` block (Plan Build Timeline + resolved rules) onto each
      // shipment of the ML1 1.0 shape. Pure projection gate; default omits it.
      const response = await apiClient.post(
        `${API_PACK_ANALYSIS_CARTONIZATION_MLINKONE_BRIDGE}?debug=1${explain ? '&explain=1' : ''}`,
        payload,
        { timeout: 60_000 }
      );
      return response.data as CartonizationBridgeEnvelope;
    } catch (error) {
      logger.error('Cartonization bridge call failed', error);
      throw error;
    }
  },

  /**
   * AutoPack 1.0 wire-shape variant — calls the bridge endpoint WITHOUT
   * `?debug=1` so the BE returns the canonical ML1 1.0 response shape
   * directly (Shipments[].Cartons[].Items[]). For external integrators
   * migrating off AutoPack 1.0; the Bridge Tester uses `cartonizeBridge`
   * (debug mode) instead so it can show trace/evidence/alternatives.
   */
  async cartonizeBridgeAutoPack(
    payload: CartonizationMLinkOneBridgeRequest
  ): Promise<Ml1AutoPackResponse> {
    try {
      const response = await apiClient.post(
        API_PACK_ANALYSIS_CARTONIZATION_MLINKONE_BRIDGE,
        payload,
        { timeout: 60_000 }
      );
      return response.data as Ml1AutoPackResponse;
    } catch (error) {
      logger.error('Cartonization bridge AutoPack call failed', error);
      throw error;
    }
  },
};

// ============================================================================
// Workbench types (spec: pack-analysis-workbench-api)
// ============================================================================

export interface AsnListItem {
  id: number;
  ingestStatus: string;
  direction: string;
  documentVariant: string;
  senderCodeRaw?: string | null;
  receiverCodeRaw?: string | null;
  buyerPartyId?: number | null;
  supplierPartyId?: number | null;
  // Resolved canonical short codes from sm_party_parties (e.g. "VNV" / "TLTM1").
  // Surfaced alongside the raw envelope long codes so operators see both at a glance.
  buyerPartyCode?: string | null;
  supplierPartyCode?: string | null;
  // Resolved ASN number from the canonical shipment row. Null until canonicalized.
  asnNum?: string | null;
  revisionNum?: number | null;
  msgSendDate?: string | null;
  ingestStartedAt?: string | null;
  ingestCompletedAt?: string | null;
  createdAt?: string | null;
  fileName?: string | null;
  primaryReason?: string | null;
  // Aggregate-count denorms stamped at canonicalize time.
  // Spec pack-analysis-asn-browser-aggregate-cells Req 4.5.
  // - cartonCount: COUNT of HU rows. Null on pre-migration / canonicalize-failed.
  // - totalQtyByUom: dict grouped by raw UoM (e.g. {"EA":1248,"CASE":12}).
  //   Null on pre-migration / parse-failure; {} on canonicalized-with-zero-items.
  cartonCount?: number | null;
  totalQtyByUom?: Record<string, number> | null;
}

export interface AsnListResponse {
  items: AsnListItem[];
  pageIndex: number;
  pageSize: number;
  totalCount: number;
  message?: string;
}

export interface AsnCanonicalHeader {
  id: number;
  ingestStatus: string;
  direction: string;
  documentVariant: string;
  senderCodeRaw?: string | null;
  receiverCodeRaw?: string | null;
  buyerPartyId?: number | null;
  supplierPartyId?: number | null;
  buyerPartyCode?: string | null;
  supplierPartyCode?: string | null;
  msgSendDate?: string | null;
  ingestStartedAt?: string | null;
  ingestCompletedAt?: string | null;
  createdAt?: string | null;
  fileName?: string | null;
  ingestDiagnosticsJsonTail?: string | null;
  ingestDiagnosticsJsonFullLength: number;
}

export interface AsnCanonicalShipment {
  id: number;
  asnNum?: string | null;
  revisionNum?: number | null;
  asnKey?: string | null;
  direction: string;
  documentVariant: string;
  senderCode?: string | null;
  receiverCode?: string | null;
  buyerPartyId?: number | null;
  supplierPartyId?: number | null;
  buyerPartyCode?: string | null;
  supplierPartyCode?: string | null;
  msgSendDateCanonical?: string | null;
  hierstruc?: string | null;
  hierarchyAnomalyNote?: string | null;
}

export interface AsnCanonicalNodeBuckets {
  s: number;
  o: number;
  p: number;
  t: number;
  i: number;
  other: number;
  total: number;
}

export interface AsnCanonicalHandlingUnit {
  id: number;
  sscc: string;
  cartonTypeId?: number | null;
  // Spec pack-analysis-hu-carton-type-dims-render — physical dims of the
  // resolved CartonType (cm + optional kg + optional m³). Null when
  // cartonTypeId is null OR the master lookup misses (defensive — backend
  // logs carton_type_lookup_miss; FE falls back to #{id}).
  cartonType?: AsnCanonicalCartonType | null;
  firstObservedAt?: string | null;
  lastObservedAt?: string | null;
  grossWeightCanonicalKg?: number | null;
  volumeCanonicalM3?: number | null;
  hierarchyAnomalyNote?: string | null;
  supplierPartyId: number;
  // Mix classification derived server-side by recomputing the carton's
  // composition signature and matching against the PackMaster rollup.
  // Null when items truncated or no PackMaster row exists.
  mixKind?: string | null;
  // Prepack identifier ("PREPACK:<code>") for PREPACK rows; null otherwise.
  packGroupKey?: string | null;
}

export interface AsnCanonicalCartonType {
  id: number;
  length: number;
  width: number;
  height: number;
  dimensionUnit: string;
  weightCapacity?: number | null;
  weightCapacityUnit?: string | null;
  volume?: number | null;
  volumeUnit?: string | null;
}

export interface AsnCanonicalPackMaster {
  id: number;
  mixKind: string;
  packGroupKey?: string | null;
  primaryItemKey?: string | null;
  compositionSignature: string;
  compositionJson: string;
  frequencyCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
  buyerPartyId?: number | null;
  supplierPartyId?: number | null;
}

export interface AsnCanonicalShipmentItem {
  id: number;
  handlingUnitId: number;
  itemId: number;
  itemCanonicalIdentifier?: string | null;
  quantity?: number | null;
  quantityUomRaw?: string | null;
  ordernumObserved?: string | null;
  // Spec pack-analysis-asn-orders-pane Req 2 — first P-ancestor node id.
  // Populated for canonicalized ASNs from 2026-04-26 onwards (after the
  // hierarchy-linkage backfill ran). Null for very old rows.
  shipmentNodeId?: number | null;
}

// Spec pack-analysis-asn-orders-pane Req 1 — one rollup row per distinct
// order. nodeId is non-null for SOPI/SEOPI/SOTPI; null for STPI (no O level)
// and for the SPOI Option-A fallback (groups by orderNum alone).
export interface AsnCanonicalOrderRollup {
  nodeId?: number | null;
  orderNum: string;
  cartonCount: number;
  itemCount: number;
  totalQty?: number | null;
}

export interface AsnCanonicalResponse {
  header: AsnCanonicalHeader;
  shipment?: AsnCanonicalShipment | null;
  nodes: AsnCanonicalNodeBuckets;
  handlingUnits: AsnCanonicalHandlingUnit[];
  handlingUnitsTotalCount: number;
  handlingUnitsTruncated: boolean;
  // Per-HU drill-down — flat list, client groups by handlingUnitId.
  shipmentItems: AsnCanonicalShipmentItem[];
  shipmentItemsTotalCount: number;
  shipmentItemsTruncated: boolean;
  itemsCount: number;
  packObservationsCount: number;
  packMasters: AsnCanonicalPackMaster[];
  // Spec pack-analysis-asn-orders-pane — distinct orders with rollup counts.
  // Empty (not undefined) when the ASN has no resolvable orders.
  orders: AsnCanonicalOrderRollup[];
  message?: string;
}

export interface PackMasterRow {
  id: number;
  mixKind: string;
  packGroupKey?: string | null;
  primaryItemKey?: string | null;
  compositionSignature: string;
  compositionJson: string;
  frequencyCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
  sourceDoc856Id?: number | null;
  buyerPartyId?: number | null;
  supplierPartyId?: number | null;
}

export interface PackMasterListResponse {
  items: PackMasterRow[];
  pageIndex: number;
  pageSize: number;
  totalCount: number;
  message?: string;
}

/** Workbench dropdown row — backs the MLinkOne Bridge Tester's
 * PreferredCartonTypeId selector. Mirrors `CartonType_OutputPayload` on the
 * server. Platform-default rows (USPS-flat-rate seeds) sort first. */
export interface CartonTypeListItem {
  id: number;
  fingerprint: string;
  description?: string | null;
  category?: string | null;
  length: number;
  width: number;
  height: number;
  dimensionUnit: string;
  weightCapacity?: number | null;
  weightCapacityUnit?: string | null;
  volume?: number | null;
  volumeUnit?: string | null;
}

// Cartonization (existing v2 endpoint — types follow the v2 contract)
export interface CartonizationLineInput {
  itemKey: string;
  /** BE field name. The pre-Spec-3b FE type was `quantity` which silently
   * became `0` in the request body (BE reads `OrderQuantity`) and undefined
   * on response payloads. Renamed to match BE wire shape so the bridge
   * tester carton row + future simulator UI both read the correct value. */
  orderQuantity: number;
  packedQuantity?: number;
  targetPackQuantity?: number | null;
  uom: string;
  /** Spec pa-shipment-aware-pack-planning — the shipment this line belongs to. The BE
   *  PackPlanningOrchestrator splits lines by `shipmentKey` and packs each shipment
   *  independently (a physical carton ships to ONE destination). Blank/undefined ⇒ a single
   *  synthetic shipment (today's behaviour, byte-identical). When more than one distinct
   *  `shipmentKey` is present, the response is the composite `CartonizationMultiShipmentResponse`. */
  shipmentKey?: string | null;
  /** Spec pa-cartongroupkey-engine-support — caller-supplied carton-separation key
   *  (e.g. Customer PO#). One level finer than `shipmentKey`: it separates cartons
   *  WITHIN a shipment. Blank/undefined ⇒ ungrouped (today's behaviour). Honored
   *  only when the request's `allowMixedCartonGroups` is `false` (strict): no carton
   *  spans two `cartonGroupKey` values and overship rolls up per group. */
  cartonGroupKey?: string | null;
  /** Spec pa-cartongroup-residual-pooling (A2) — OUTPUT-only per-inner-item PO-line
   *  reference on response carton items. With `cartonGroupKey` (and the shipment) it gives
   *  each carton item a `(shipmentKey, cartonGroupKey, sourceLineIndex)` origin so a pooled
   *  shared carton's units trace back to each PO line. `null` when the strategy did not
   *  attribute the item to a single source line (legacy / unpooled paths). Reconciliation
   *  attributes pooled cartons per `(itemKey, cartonGroupKey)` — already correct per CPO; this
   *  field is for display + traceability. */
  sourceLineIndex?: number | null;
  /** Spec pa-source-line-attribution — caller-supplied OPAQUE per-PO-line reference (the Portal
   *  Item Key). INPUT: set per line by the caller/integration; the engine passes it through
   *  VERBATIM. OUTPUT: echoed on each carton inner item this line contributed — the authoritative
   *  token for ASN round-trip (join a carton's units back to the full PO-line dataset by this key,
   *  alongside the run's `shipmentKey` = Portal Shipment Key). Independent of `cartonGroupKey`
   *  (identity vs business separation). `null` when the line had none (then `sourceLineIndex` —
   *  per-shipment, 0-based — is the positional fallback). */
  sourceLineRef?: string | null;
  /** Spec pack-analysis-pack-type-gating — caller-declared pack type.
   *  "B" = Bulk; "P" = Prepack; null/undefined = legacy (decide from history).
   *  Bridge tester does NOT need to set this — the BE adapter auto-derives
   *  from PrepackCode presence (TIGHTENED 2026-05-02 by spec
   *  pack-analysis-buyer-pack-instructions: requires PackFactor too).
   *  Set explicitly only when calling the canonical /cartonization/suggest
   *  endpoint with mixed bulk/prepack lines. */
  packType?: 'B' | 'P' | null;

  /** Spec pack-analysis-buyer-pack-instructions — buyer-mandated units of
   *  this line per shipping carton. When set, the BE engine activates
   *  `BuyerMandatedPackInstructionStrategy` at Order=0, ahead of every
   *  observation-based strategy. On miss / invariant failure the line falls
   *  through to the existing chain unchanged. Must be ≥ 1 when set. */
  casePackQty?: number | null;

  /** Spec pack-analysis-buyer-pack-instructions — buyer-mandated units of
   *  this line per inner-pack inside the case. Informational only — stamped
   *  on the carton outcome's reasonArgs.innerPackQty for trace-inspector
   *  visibility but NEVER consulted as a search dimension. Must be ≥ 1 when
   *  set; CasePackQty must also be set; CasePackQty % InnerPackQty == 0. */
  innerPackQty?: number | null;

  /** Spec pack-analysis-prepack-by-group-key — prepack code for this line; the
   *  BE prefixes it `"PREPACK:"` and matches the PackMaster `pack_group_key`.
   *  Set together with `packFactor`. The Simulator surfaces both as per-line
   *  advanced fields; the Bridge Tester uses its own `CartonizationMLinkOneBridgeLine.prepackCode`. */
  packGroupKey?: string | null;
  /** Spec pack-analysis-prepack-by-group-key — units per prepack instance. */
  packFactor?: number | null;

  /** Spec pack-analysis-constraint-solver-v1 — per-line physical override.
   *  When the resolver can't find dims/weight for this item, the solver uses
   *  these. Surfaced in the Simulator's per-line advanced disclosure.
   *  Spec pack-analysis-cartonization-unit-aware-contract: the values are
   *  interpreted in `dimensionUnit` / `weightUnit` (per-line override of the
   *  request-level unit), else in the request-level `CartonizationRequest.dimensionUnit`
   *  / `weightUnit`, else cm/kg. When echoed inside a response carton's `items`,
   *  the BE re-expresses them in the request's output unit and stamps these. */
  length?: number | null;
  width?: number | null;
  height?: number | null;
  weight?: number | null;
  /** Per-line unit override for `length`/`width`/`height` (cm/mm/m/in/ft + verbose
   *  aliases; case-insensitive). Null → request-level → "cm". Unknown → -4913. */
  dimensionUnit?: string | null;
  /** Per-line unit override for `weight` (kg/g/lb/oz + aliases). Null → request-level → "kg". */
  weightUnit?: string | null;
}

/** A bare length/width/height triple — the unit is carried on the parent
 *  `CartonizationCarton.dimensionUnit` (mirrors `CartonType`). */
export interface CartonizationDimEstimate {
  length: number;
  width: number;
  height: number;
}

export interface CartonizationRequest {
  tenantId: number;
  /** Required by the BE handler (`Cartonization_Suggest_Query` rejects an empty
   * partitionKey with Invalid_Request). For the SmartMatrix tenant this is
   * `sys:smart_matrix` (`PARTITION_KEY` constant). */
  partitionKey?: string;
  /** BE field names are `BuyerCode` / `SupplierCode` (case-insensitive bind). */
  buyerCode?: string | null;
  supplierCode?: string | null;
  buyerPartyId?: number | null;
  supplierPartyId?: number | null;
  /** @deprecated The BE has no `BuyerPartyCode`/`SupplierPartyCode` — use
   * `buyerCode`/`supplierCode`. Kept only so older callers don't break. */
  buyerPartyCode?: string | null;
  supplierPartyCode?: string | null;
  purchaseOrderNumber?: string | null;
  /** Not consumed by the BE today (no `EffectiveAt` on `Cartonization_Suggest_Request`);
   * carried for forward-compat / UI display only. */
  effectiveAt?: string | null;
  lines: CartonizationLineInput[];

  // ── Spec 3b — opt-in enrichment flags (default false) ────────────────────
  // None of these change the canonical Cartons[] payload; each populates an
  // additional response field. Matches BE Cartonization_Suggest_Request.
  includeEvidence?: boolean;
  includeUtilization?: boolean;
  includeAlternatives?: boolean;

  /** Spec pack-analysis-asn-derived-packfactor — opt out of ASN-observed
   * PackFactor pre-fill on Prepack lines whose PackFactor is null. Default
   * `true` server-side when omitted; only set explicitly to `false` to
   * reproduce pre-spec behaviour (no pre-fill, no divergence detection). */
  useObservedPackFactor?: boolean;

  /** Spec pack-analysis-prepack-resolver-visibility (strict mode). Default
   * `false`. When `true` AND any line carries a non-empty `packGroupKey`,
   * `PrepackRoundupStrategy` STOPS the resolver cascade at the strict
   * rejection from the PackGroupKey resolver instead of falling through
   * to the ObservedHistory resolver. The strategy returns Skipped with
   * reason `PREPACK_GROUP_KEY_STRICT_FAIL`. Use this when `packGroupKey` is
   * a contract: "match by this code or fail." */
  useStrictPackGroupKey?: boolean;

  /** Spec pack-analysis-prepack-buyer-cascade — per-call override of the resolved
   * `PackagingRuleProfile.prepackBuyerCascade` flag. `null`/omitted = inherit the
   * resolved profile's value (byte-equal to today). `true` / `false` = force the
   * PREPACK-side cross-supplier cascade on/off for this run. When effective-true,
   * `PackGroupKeyPrepackResolver` may, on a strict `(tenant, buyer, supplier,
   * "PREPACK:"+code)` miss, fall back to the best buyer-only `(tenant, buyer, *,
   * "PREPACK:"+code)` match across suppliers — an APPROXIMATE suggestion only
   * (confidence capped, `requiresAcknowledgment=true`, tier `T2-PC`
   * (post-rename — see `@/lib/pack-analysis/tierCodes`), reason code
   * `MATCHED_PREPACK_BY_GROUP_KEY_CROSS_SUPPLIER`, visible warning). The Simulator
   * + MLinkOne Bridge Tester expose a tri-state control bound to this. */
  prepackBuyerCascade?: boolean | null;

  /** Bulk-side mirror of `prepackBuyerCascade` — per-call override of the
   * resolved `PackagingRuleProfile.buyerPatternCascade` flag. `null`/omitted =
   * inherit the resolved profile's value; `true` / `false` = force the BULK-side
   * cross-supplier cascade on/off for this run (T2-BC / T2-BCM). */
  bulkBuyerCascade?: boolean | null;

  /** Channel routing axis (e.g. `"DC_DIRECT"` / `"STORE_DIRECT"`). Threaded to
   * BE packaging-rule + key-composition resolvers. `null`/omitted = channel-agnostic
   * (matches pre-Spec-3b behaviour). */
  channelCode?: string | null;

  /** Spec pack-analysis-constraint-solver-v1 — opt into the Free-Mix bin-packing
   * solver (tier `T4`, post-rename via `@/lib/pack-analysis/tierCodes`).
   * `null`/omitted → resolved from the profile. Gates mixing of different ITEM KEYS
   * in one carton. (Renamed from `allowMixPack` by pa-cartongroupkey-engine-support
   * to match the renamed BE canonical field; the legacy bridge wire field keeps its
   * name.) */
  allowMixedItemKeys?: boolean | null;
  /** Spec pa-cartongroupkey-engine-support — the orthogonal carton-mix axis: may two
   * lines that differ by `cartonGroupKey` (e.g. Customer PO#) share one carton?
   * `null`/omitted → resolved from the profile, default `true` (permissive = today's
   * behaviour, byte-identical). Set `false` for strict carton-group separation. */
  allowMixedCartonGroups?: boolean | null;
  /** Spec pack-analysis-constraint-solver-v1 — pin the solver's carton type. */
  preferredCartonTypeId?: number | null;

  /** Spec pack-analysis-cartonization-unit-aware-contract — "the unit this caller
   * speaks": the default interpretation for any per-line override value that
   * omits its own `dimensionUnit`, AND the unit every carton's `estimatedDim`
   * (and echoed line override) comes back in. Case-insensitive (cm/mm/m/in/ft +
   * verbose aliases). `null`/omitted → "cm" — no conversion, byte-equal to the
   * pre-spec behaviour. Unknown → request fails with statusCode -4913. */
  dimensionUnit?: string | null;
  /** Spec pack-analysis-cartonization-unit-aware-contract — same for weight
   * (kg/g/lb/oz + aliases). `null` → "kg". */
  weightUnit?: string | null;
}

export interface CartonizationCarton {
  index: number;
  cartonTypeId?: number | null;
  cartonTypeCode?: string | null;
  source?: string | null;
  /** Cartonization tier — wire-level type is `string` so we can safely accept
   * unknown values from older BE deploys during a rolling rollout. Validated
   * callers should narrow against `ALL_TIER_CODES` from
   * `@/lib/pack-analysis/tierCodes` and treat the result as `TierCode`.
   * Spec pack-analysis-tier-code-rename (2026-05-13). */
  tier?: string | null;
  mixKind?: string | null;
  compositionSignature?: string | null;
  /** BE serializes Cartonization_SuggestedCarton.Items as `items` (camelCase).
   * Pre-Spec-3b this field was incorrectly typed as `lines` and never observed
   * because no UI consumed it. The Bridge Tester surfaces the per-carton item
   * count, so the field name is now corrected to match the BE wire shape. */
  items: CartonizationLineInput[];
  /** Estimated outer dimensions of this carton, in `dimensionUnit` (the request's
   *  declared unit, default "cm"). `Cartonization_DimEstimate` is a bare
   *  `{length,width,height}` triple — the unit lives on the carton.
   *  (Spec pack-analysis-cartonization-unit-aware-contract; renamed from the
   *  never-wired `lengthMm`/`widthMm`/`heightMm` trio.) */
  estimatedDim?: CartonizationDimEstimate | null;
  /** Estimated gross weight of this packed carton, in `weightUnit` (default "kg").
   *  (Renamed from the never-wired `grossKg`.) */
  estimatedWeight?: number | null;
  /** Unit of `estimatedDim` — the request's effective dimension unit. Non-null
   *  whenever `estimatedDim` is non-null. */
  dimensionUnit?: string | null;
  /** Unit of `estimatedWeight` — the request's effective weight unit. */
  weightUnit?: string | null;
  /** Spec `pack-analysis-carton-multiplicity-output` — number of physical
   * cartons this entry represents. Default 1; emitted on every entry by the
   * BE dedup pass. When > 1 the carton row is run-length-encoded and the
   * `items` list describes a SINGLE carton's composition (not multiplied).
   * Item-level totals across all N cartons = `items[i].orderQuantity * cartonCount`.
   * Mirrors SAP EWM HU-template semantics. */
  cartonCount?: number | null;
  /** Strategy-emitted confidence in [0, 1] for this carton's plan. Bulk T2-BS
   * tops out at 0.99; ConstraintSolver T4 caps at 0.65; Buyer-Mandated T1 at
   * 0.95 (post-rename — spec pack-analysis-tier-code-rename). Surfaced
   * per-card on AlternativesPanel so operators can compare trade-offs. Always
   * populated by the BE; older fixtures may omit. */
  confidence?: number | null;
  /** Free-Mix cartons (and any algorithmically-fabricated plan) always carry
   * `requiresAcknowledgment=true` (architecture lock). The Simulator surfaces
   * it as a flag on the outcome banner. */
  requiresAcknowledgment?: boolean | null;
  /** Provenance of this carton's dimensions: observation | caller | profile |
   * tenant | platform-default | solver | none | approved_list. Shown as a small hint on the
   * Suggested-cartons cards. (Spec pack-analysis-non-solver-carton-dim-enrichment +
   * pack-analysis-rounding-emission-redesign.) */
  dimSource?: string | null;
  /** Spec pack-analysis-rounding-emission-redesign R-3 (G3) — stamped `true`
   * ONLY when this carton is the sub-emission-size "partial last carton"
   * emitted under `RoundingPolicy=ROUND_UP` + `RoundingTargetUom=INNER` with
   * a non-zero residual at inner-pack granularity. FE renders distinctly
   * — smaller card + "Partial carton" badge — so operators see the
   * sub-case unit at a glance. Default `false`. */
  isPartialLastCarton?: boolean | null;
  /** Per-carton trace — observation / constraint / result keys that apply to
   *  THIS carton specifically. Mirrors the rung-level `reasonArgs` shape but
   *  at carton granularity so the per-row Why panel can surface the right
   *  chips per carton without polluting cartons emitted by other item groups
   *  in the same rung (live-demo refinement 2026-05-25). Null when the
   *  strategy didn't opt in (T4 solver, legacy paths); FE callers fall back
   *  to the rung-level reasonArgs in that case. */
  reasonArgs?: Record<string, string> | null;
  /** Spec pack-analysis-fallback-strategy Req 7 / Req 11.1 — original
   * position of the source `lines[i]` (0-indexed) that produced this carton.
   * Null when the strategy didn't preserve source-line attribution (T1/T2/T4
   * legacy outputs). T5 OneSkuPerCartonStrategy stamps the first contributing
   * line per Req 7.4 so the cartons list is sortable by PO line order.
   * Mainstream parallel: SAP POSNR / Walmart EDI 856 POLin / Oracle
   * LINE_NUMBER / Manhattan customer-provided sequence. */
  sourceLineIndex?: number | null;
}

// ── Spec 3b enrichment DTOs (additive on the wire) ─────────────────────────

export interface CartonizationPatternRef {
  packMasterId: number;
  compositionSignature: string;
  frequency: number;
  lastObservedUtc?: string | null;
  mixKind: string;
}

export interface CartonizationLineEvidence {
  observationCount: number;
  firstSeenUtc?: string | null;
  lastSeenUtc?: string | null;
  topPatterns: CartonizationPatternRef[];
  /** Cartonization fallback tier for this line — wire-level `string` so we
   * tolerate unknown values from older BE deploys. Narrow against
   * `ALL_TIER_CODES` from `@/lib/pack-analysis/tierCodes` before treating
   * as `TierCode`. Spec pack-analysis-tier-code-rename (2026-05-13). */
  fallbackTier: string;
  confidenceFormula: string;
  /** Spec pack-analysis-asn-derived-packfactor — observed-derived PackFactor
   * for this item under the resolved (buyer, supplier, prepack_code) tuple.
   * Null when no PackGroupKey was supplied OR no PackMaster row matched.
   * Populated regardless of whether pre-fill fired — Bridge Tester needs
   * the value even when caller already supplied PackFactor. */
  derivedPackFactor?: number | null;
  /** Spec pack-analysis-asn-derived-packfactor — confidence band of the
   * derived value: "high" / "medium" / "low" / "drift" / null. */
  derivedPackFactorConfidence?: string | null;
}

export interface CartonizationCartonUtilization {
  cubeUtilizationPct?: number | null;
  voidFillPct?: number | null;
  weightUtilizationPct?: number | null;
  weightCapKgUsed?: number | null;
  estimatedInnerVolumeCm3?: number | null;
  cartonInteriorVolumeCm3?: number | null;
}

export interface CartonizationAlternative {
  strategy: string;
  /** Cartonization tier — wire-level `string` (see `CartonizationCarton.tier`
   * for the post-rename narrowing convention). */
  tier: string;
  source: string;
  mixKind: string;
  cartons: CartonizationCarton[];
  reason: string;
  relativeRank: number;
}

export interface CartonizationTraceEntry {
  strategy: string;
  /** Cartonization tier — wire-level `string` (see `CartonizationCarton.tier`
   * for the post-rename narrowing convention). May be the skipped sentinel
   * `T-` when the strategy returned `Status: Skipped`. */
  tier: string;
  reasonCode: string;
  reasonArgs?: Record<string, string> | null;
  evaluatedCandidatesCount: number;
  elapsedMs: number;
  outcome: string;

  // ── Spec pack-planner-build-timeline (Phase 0) — per-rung residual ──
  // Drives the §0 Plan Build Timeline's IO sparkbar.
  // Semantics differ by request mode (PROGRESSIVE vs ALTERNATIVES):
  //   - Progressive: actual consumption story (residual decreases across chain)
  //   - Alternatives: every rung sees total request units; "would have packed" framing
  // See Cartonization_SuggestionTrace.cs comment block for full detail.
  unitsInputAtRungStart?: number | null;
  unitsConsumedByRung?: number | null;
}

export interface CartonizationSuggestionTrace {
  runId: string;
  evaluatedStrategies: CartonizationTraceEntry[];
  resolvedProfileId?: number | null;
  resolvedProfileScopePath?: string | null;
  resolvedRoundingPolicy?: string | null;
  resolvedRoundingTargetUom?: string | null;
  resolvedCartonPolicy?: string | null;
  resolvedBuyerPatternCascade?: boolean | null;
  channelCode?: string | null;

  // ── Spec pack-planner Phase 2.A — additive Buyer-trust evidence fields ──
  // All nullable; populated only when a packaging-rule profile resolves AND
  // the caller opts into the trace (IncludeEvidence). Non-Pack-Planner
  // callers see byte-equal responses (Trace itself stays null).
  resolvedMaxCartonWeightKg?: number | null;
  resolvedMinCartonFillPct?: number | null;
  resolvedMixPackPolicy?: string | null;
  resolvedApprovedCartonCount?: number | null;
  resolvedBuyerPartyName?: string | null;
}

/**
 * V.28 / pack-planner-warning-localisation Phase A — machine-readable
 * companion to a single `shipmentWarnings[i]` entry. Emitted only by
 * BE strategies that have been migrated to the structured shape
 * (Phase A = OneSkuPerCartonStrategy only); when present the FE renders
 * via `cartonization.warnings.byCode.{code}` with `args` interpolated,
 * falling back to `legacyText` when the locale template is missing.
 */
export interface ShipmentWarning {
  code: string;
  args: Record<string, string>;
  legacyText: string;
}

export interface CartonizationResponse {
  cartons: CartonizationCarton[];
  /** BE field is `shipmentWarnings` — soft warnings from the orchestrator
   * (line/order overship, prepack round-up, PackGroupKey downgrade,
   * observed-PackFactor drift / divergence). The pre-spec field name was
   * `warnings`; it never matched the BE shape, so it was dropped silently.
   * Renamed 2026-04-30 to fix that gap. */
  shipmentWarnings?: string[];
  /**
   * V.28 / pack-planner-warning-localisation Phase A — structured equivalents
   * of `shipmentWarnings` from migrated strategies. `null`/absent when no
   * migrated rung emitted a warning; in that case FE renders `shipmentWarnings`
   * strings verbatim (back-compat). When non-empty, FE renders via locale
   * template lookup; per-entry `legacyText` is the graceful fallback.
   */
  structuredWarnings?: ShipmentWarning[] | null;
  message?: string;
  /** Server-assigned run identifier for trace correlation; present on enriched responses. */
  runId?: string;

  // ── Spec 3b — additive enrichment fields. All nullable; populated only when
  // the matching opt-in flag is set on the request. P1 invariant: when no
  // flags are set, this object is byte-equal to the pre-Spec-3b response
  // (modulo runId).
  lineEvidence?: Record<string, CartonizationLineEvidence> | null;
  cartonUtilization?: CartonizationCartonUtilization[] | null;
  alternatives?: CartonizationAlternative[] | null;
  trace?: CartonizationSuggestionTrace | null;

  // ── Spec pack-analysis-mlinkone-bridge-autopack-compat — populated by the
  // bridge endpoint when called with ?debug=1. Null for canonical (non-bridge)
  // callers and for default-mode bridge calls (those return the projection as
  // the response BODY, not a sibling).
  // ⚠ CASING (spec mlinkone-bridge-tester-multi-shipment D1): on THIS debug
  // envelope `ml1Shape` is serialized **camelCase** (the default API JSON
  // policy via `Ok(pipelineResult)`), NOT the PascalCase `Ml1AutoPackResponse`
  // that the NON-debug native body uses (forced by the bridge's _ml1JsonOptions).
  // Typed access must therefore use the camelCase `Ml1ShapeCamel` mirror.
  ml1Shape?: Ml1ShapeCamel | null;
}

/**
 * Spec pa-shipment-aware-pack-planning — the canonical-native composite returned by the
 * `/cartonization` endpoint when a request fans out to MORE THAN ONE shipment. A single-shipment
 * (or no-`shipmentKey`) request keeps returning a plain `CartonizationResponse` (byte-identical),
 * so existing callers are unaffected. NOT the ML1 wire shape — Pack Planner / ML2 are direct
 * canonical callers, so each shipment carries the full canonical response.
 */
export interface CartonizationMultiShipmentResponse {
  /** Per-shipment plans in first-seen `shipmentKey` order. */
  shipments: CartonizationShipmentPlan[];
}

export interface CartonizationShipmentPlan {
  /** The shipment's key (null = the synthetic single shipment when none was supplied). */
  shipmentKey?: string | null;
  /** The full canonical cartonization response for this shipment alone. */
  plan: CartonizationResponse;
}

// ── Spec mlinkone-bridge-tester-multi-shipment (D1 — the casing trap) ──
// camelCase mirror of the ML1 1.0 AutoPack shape AS IT APPEARS ON THE DEBUG
// ENVELOPE (`CartonizationResponse.ml1Shape`). Distinct from the PascalCase
// `Ml1AutoPackResponse` below (the non-debug native bridge body). The debug
// envelope is serialized with the default API policy = camelCase, so reads here
// MUST use camelCase field names — `data.ml1Shape.Shipments` (PascalCase) is
// `undefined` at runtime. The `_`-prefixed PA-extension carton fields keep their
// casing in BOTH serializations.
export interface Ml1ShapeCamel {
  statusCode?: string;
  message?: string;
  shipments: Ml1ShipmentCamel[];
}

export interface Ml1ShipmentCamel {
  shipmentKey: string;
  cartons: Ml1CartonCamel[];
  /** Per-shipment Explain block (trace + planLadder) when ?explain=1; loosely
   *  typed — the Tester surfaces it via the ML1 1.0 shape JSON view. */
  explain?: Record<string, unknown> | null;
}

export interface Ml1CartonCamel {
  cartonCode: string;
  cartonCount: number;
  items: Ml1CartonItemCamel[];
  // `_`-prefixed PA-extension fields — casing preserved in both serializations.
  _Strategy?: string | null;
  _Tier?: string | null;
  _MixKind?: string | null;
  _Confidence?: number | null;
  _DimSource?: string | null;
  _WeightSource?: string | null;
}

export interface Ml1CartonItemCamel {
  upc?: string | null;
  sku?: string | null;
  ean?: string | null;
  packType?: string | null;
  packedQuantity: number;
}

// ── Spec pack-analysis-mlinkone-bridge-autopack-compat — AutoPack 1.0 wire
// shape (PascalCase). Matches edi-samples/autopack/response/autopack_response_1.json.
// Returned by the bridge endpoint when called WITHOUT ?debug=1, or as the
// `ml1Shape` sibling on the rich envelope when called WITH ?debug=1.

export interface Ml1AutoPackResponse {
  StatusCode: string;
  Message: string;
  Shipments: Ml1Shipment[];
}

export interface Ml1Shipment {
  ShipmentKey: string;
  ShipDate: string | null;
  ShipTo: string;
  Store: string;
  ShipMode: string;
  Cartons: Ml1Carton[];
  /**
   * Spec mlinkone-bridge-explain-block — opt-in per-shipment "why" block,
   * present only when the bridge was called with `explain=1`. PascalCase
   * (native-shape casing) — the reused trace differs from the camelCase
   * Pack Planner `data.trace`.
   */
  Explain?: Ml1ExplainBlock | null;
}

/**
 * Spec mlinkone-bridge-explain-block — Plan Build Timeline + resolved rules.
 * `Trace` mirrors the BE `Cartonization_SuggestionTrace` but serialized
 * **PascalCase** under the bridge's `_ml1JsonOptions` (`RunId`,
 * `EvaluatedStrategies[]`, `Resolved*`...), not the camelCase FE
 * `CartonizationSuggestionTrace`. The Tester only stringifies it for display,
 * so it is typed loosely here rather than asserting the wrong casing.
 */
export interface Ml1ExplainBlock {
  SchemaVersion: number;
  Trace: Record<string, unknown>;
  /**
   * Spec mlinkone-bridge-plan-ladder — server-computed, render-ready full ladder
   * (every chain rung in order, incl. the ones that did not run). Present only
   * when the bridge was called with `explain=1`. PascalCase (native-shape casing).
   */
  PlanLadder?: Ml1PlanLadder | null;
}

/**
 * Spec mlinkone-bridge-plan-ladder — the full ordered cartonization chain
 * projected onto the bridge Explain block. PascalCase (wire shape). Each rung is
 * render-ready: status synthesised server-side, residual-flow + per-rung packed
 * already computed, stable `NameId` + English `NameDefault` for downstream i18n.
 */
export interface Ml1PlanLadder {
  WinningRungOrder: number | null;
  PlanBuiltMs: number | null;
  Rungs: Ml1PlanLadderRung[];
}

export interface Ml1PlanLadderRung {
  Order: number;
  Label: string;
  TierCode: string | null;
  NameId: string;
  NameDefault: string;
  /** MATCHED | NO_MATCH | NOT_RUN | NOT_IMPLEMENTED | SKIPPED_BY_CRITERIA | SKIPPED_BY_NOT_IMPLEMENTED */
  Status: string;
  ShortCircuitedAfter: number | null;
  UnitsIn: number | null;
  UnitsOut: number | null;
  ReasonCode: string | null;
  ReasonArgs: Record<string, string> | null;
  ElapsedMs: number | null;
  EvaluatedCandidatesCount: number | null;
  Packed?: Ml1RungPacked | null;
  SubBehaviours: Ml1PlanLadderSubBehaviour[];
}

export interface Ml1RungPacked {
  CartonCount: number;
  UnitsPacked: number;
  ByCarton: Ml1RungCartonBreakdown[];
}

export interface Ml1RungCartonBreakdown {
  CartonCode: string | null;
  Count: number;
  Units: number;
}

export interface Ml1PlanLadderSubBehaviour {
  Tier: string;
  NameId: string;
  NameDefault: string;
}

export interface Ml1Carton {
  CartonCode: string;
  Length: number | null;
  Width: number | null;
  Height: number | null;
  DimensionUnit: string;
  GrossWeight: number | null;
  TypeWeigh: number | null;
  NetWeight: number | null;
  NetNetWeight: number | null;
  WeightUnit: string;
  Volume: number | null;
  VolumeUnit: string | null;
  LabelFormatId: number | null;
  CartonCount: number;
  Items: Ml1CartonItem[];
}

export interface Ml1CartonItem {
  POHeaderId: number | null;
  OrderNum: string;
  RevisionNum: number;
  PODetailId: number | null;
  PackType: string;          // "B" or "P"
  BulkpackItemKey: string;
  PrepackItemKey: string;
  UPC: string | null;
  SKU: string | null;
  EAN: string | null;
  Style: string | null;
  Color: string | null;
  Size: string | null;
  ShortDesc: string | null;
  FullDesc: string | null;
  PrepackCode: string | null;
  PrepackStyle: string | null;
  PackFactor: number;
  PackedQuantity: number;
  NetWeight: number | null;
  NetNetWeight: number | null;
  WeightUnit: string;
  CartonCapacity: number | null;
  /** Portal 2.0 line-item identifier. Always present (null when not supplied
   * on the source line) — extends the canonical AutoPack 1.0 response shape. */
  PortalItemKey: number | null;
}

// ── Spec 3b — MLinkOne 1.0 bridge request DTOs (mirror BE
// Cartonization_MLinkOneBridge_Request). The bridge takes the legacy 4-field
// flat-string item-key shape (sku/upc/ean/style) and synthesizes a canonical
// ItemKey per line via Spec 3a's DB-driven rule store, so MLinkOne 1.0
// callers never compose keys client-side.
export interface CartonizationMLinkOneBridgeLine {
  sku?: string | null;
  upc?: string | null;
  ean?: string | null;
  /** VNV-style ItemKey fallback driver. Empty/null for buyers whose seeded
   * rule resolves via SKU/UPC/EAN. */
  style?: string | null;
  orderQuantity: number;
  packedQuantity: number;
  targetPackQuantity?: number | null;
  uom: string;

  // ── Spec pack-analysis-mlinkone-bridge-autopack-compat — AutoPack 1.0 /
  // Portal 2.0 caller identity. All optional, all opaque pass-through; never
  // participate in PA cartonization logic, only round-tripped onto each
  // output item in the projected ML1 1.0 wire shape.
  poHeaderId?: number | null;
  poDetailId?: number | null;
  /** Per-line PO number for multi-PO calls. Falls back to request-level
   * `purchaseOrderNumber` when null/empty. */
  orderNum?: string | null;
  revisionNum?: number | null;
  shipmentKey?: string | null;
  /** Spec pa-bridge-cartongroup-sourceline-parity — opaque carton-group key
   *  (caller's Composite Key Profile). Pass-through verbatim onto the canonical
   *  line; engine never interprets it. Null/empty ⇒ ungrouped. */
  cartonGroupKey?: string | null;
  shipDate?: string | null;
  shipTo?: string | null;
  store?: string | null;
  shipMode?: string | null;
  color?: string | null;
  size?: string | null;
  shortDesc?: string | null;
  fullDesc?: string | null;
  prepackCode?: string | null;
  prepackStyle?: string | null;
  packFactor?: number | null;
  /** Spec pack-analysis-buyer-pack-instructions — buyer-mandated units per
   *  shipping carton. Bridge passes through verbatim onto
   *  `CartonizationLineInput.casePackQty`; canonical validator enforces ≥ 1. */
  casePackQty?: number | null;
  /** Spec pack-analysis-buyer-pack-instructions — buyer-mandated units per
   *  inner-pack inside the case. Bridge passthrough; informational on output. */
  innerPackQty?: number | null;
  /** Portal 2.0 line-item identifier. */
  portalItemKey?: number | null;
  /** Caller-supplied bulk-pack identity. Echoed onto output when PackType="B". */
  bulkpackItemKey?: string | null;
  /** Caller-supplied prepack-group identity. Echoed onto output when PackType="P". */
  prepackItemKey?: string | null;

  // Spec pack-analysis-constraint-solver-v1 — per-line dim override panel.
  // When any of the four fields is set, the ConstraintSolver uses these in
  // place of the UoM-ladder-resolved item dims. Optional; null = ladder lookup.
  /** Length in `dimensionUnit` (default: cm). */
  length?: number | null;
  /** Width in `dimensionUnit` (default: cm). */
  width?: number | null;
  /** Height in `dimensionUnit` (default: cm). */
  height?: number | null;
  /** Weight in `weightUnit` (default: kg). */
  weight?: number | null;
  /** Unit for length fields. Supported: cm / mm / m / in / ft.
   *  Null / empty → cm (default). Unknown unit → bridge fails with -4913. */
  dimensionUnit?: string | null;
  /** Unit for the weight field. Supported: kg / g / lb / oz. Null / empty → kg. */
  weightUnit?: string | null;
}

export interface CartonizationMLinkOneBridgeRequest {
  partitionKey?: string;
  tenantId: number;
  purchaseOrderNumber: string;
  buyerPartyId?: number | null;
  buyerCode?: string | null;
  supplierPartyId?: number | null;
  supplierCode?: string | null;
  lines: CartonizationMLinkOneBridgeLine[];
  suggestionCriteria?: unknown;          // reuses canonical Cartonization_SuggestionCriteria; bridge tester doesn't tune it
  includeUomLadder?: boolean;
  includeEvidence?: boolean;
  includeUtilization?: boolean;
  includeAlternatives?: boolean;
  /** Spec pack-analysis-prepack-resolver-visibility (strict mode) — when
   *  `true` AND any line carries a non-empty `prepackCode`, the bridge sets
   *  `useStrictPackGroupKey=true` on the canonical request. Default `false`. */
  useStrictPackGroupKey?: boolean;
  channelCode?: string | null;
  /** Spec pa-bridge-cartongroup-sourceline-parity — mirrors the canonical
   *  `allowMixedCartonGroups`. Omitted ⇒ true (permissive); `false` ⇒ strict
   *  per-CartonGroupKey separation. Only meaningful when lines supply
   *  `cartonGroupKey`. */
  allowMixedCartonGroups?: boolean | null;
  /** Spec pack-analysis-constraint-solver-v1 — per-call opt-in for the
   *  Free-Mix solver. `null` = use buyer profile MixPackPolicy default
   *  (Independent → solver short-circuits with MIX_PACK_OPT_OUT). */
  allowMixPack?: boolean | null;
  /** Spec pack-analysis-constraint-solver-v1 — preferred CartonType for the
   *  solver. `null` = use auto-selection cascade (buyer history → tenant
   *  history → hardcoded default fingerprint). */
  preferredCartonTypeId?: number | null;
  /** Spec pack-analysis-prepack-buyer-cascade — passthrough to the canonical
   *  request's `prepackBuyerCascade`. `null`/omitted = inherit the resolved
   *  profile flag; `true` / `false` = force the PREPACK-side cross-supplier
   *  cascade on/off for this run. The Bridge Tester exposes a tri-state control. */
  prepackBuyerCascade?: boolean | null;
  /** Bulk-side mirror of `prepackBuyerCascade` — per-run override of the
   *  resolved profile's `buyerPatternCascade` (BULK cross-supplier cascade). */
  bulkBuyerCascade?: boolean | null;
}

/** Result envelope returned by `cartonizeBridge`. The bridge tester renders
 * `succeeded=false` shapes verbatim (status -4910 / -4902 / etc.) instead of
 * unwrapping `data` like other service methods do. */
export interface CartonizationBridgeEnvelope {
  succeeded: boolean;
  statusCode?: number | null;
  messages?: string[];
  data?: CartonizationResponse | null;
}

// ── Spec pack-analysis-bridge-native-autopack-request-shape ────────────────
// AutoPack 1.0 / AutoPlan native shape mirroring the legacy DTOs at
//   edi-samples/legacy-systems/MLinkOneV1/Source/Domain/Messages/External/AutoPlan/
// PascalCase JSON keys, nested 3-level (Cond.Orders[].Details[]). The Bridge
// Tester serializes its form state into this shape on Send + the request
// preview / Copy as cURL. The internal JWT endpoint sniffs `Cond` to dispatch
// to the AutoPack adapter; the external API Key endpoint accepts this shape
// only.
export interface CartonizationAutoPackBridgeRequest {
  Cond: AutoPackBridgeCondition;
  /** Source-system id; legacy AutoPlan defaults to "EL1". Logged as
   * `route_tag` server-side, never branched on. */
  DataSource: string;
  /** Spec pack-analysis-prepack-buyer-cascade — optional per-run override of the
   * resolved profile's `prepackBuyerCascade` flag. `null`/omitted (what production
   * ML1 1.0 sends) = inherit the profile flag; `true` / `false` = force the
   * PREPACK-side cross-supplier cascade on/off. The Bridge Tester exposes a
   * tri-state control bound to this. */
  PrepackBuyerCascade?: boolean | null;
  /** Bulk-side mirror of `PrepackBuyerCascade` — per-run override of the
   * resolved profile's `BuyerPatternCascade` (BULK cross-supplier cascade). */
  BulkBuyerCascade?: boolean | null;
  /** Spec pa-bridge-cartongroup-sourceline-parity — mirrors the canonical
   * `allowMixedCartonGroups`. Omitted ⇒ true (permissive); `false` ⇒ strict
   * per-CartonGroupKey separation. */
  AllowMixedCartonGroups?: boolean | null;
}

export interface AutoPackBridgeCondition {
  /** Buyer long code, e.g. "VINEYARDVINE". */
  Buyer: string | null;
  /** Supplier (vendor) long code, e.g. "TLTM1FACTORY". */
  Vendor: string | null;
  Orders: AutoPackBridgeOrder[];
}

export interface AutoPackBridgeOrder {
  POHeaderId: number;
  OrderNum: string | null;
  RevisionNum: number;
  Details: AutoPackBridgeOrderDetail[];
}

export interface AutoPackBridgeOrderDetail {
  PODetailId: number;
  ShipmentKey: string | null;
  /** Spec pa-bridge-cartongroup-sourceline-parity — opaque carton-group key,
   *  pass-through verbatim onto the canonical line (engine never interprets it;
   *  not derived from OrderNum). Null/empty ⇒ ungrouped. */
  CartonGroupKey?: string | null;
  ShipDate: string | null;
  ShipTo: string | null;
  Store: string | null;
  ShipMode: string | null;
  PackType: string | null;
  BulkpackItemKey: string | null;
  PrepackItemKey: string | null;
  UPC: string | null;
  SKU: string | null;
  EAN: string | null;
  Style: string | null;
  Color: string | null;
  Size: string | null;
  ShortDesc: string | null;
  FullDesc: string | null;
  PrepackCode: string | null;
  PrepackStyle: string | null;
  /** Legacy `decimal`. Use 0 as the "not set" sentinel — the BE adapter maps
   *  0 → null when populating the canonical `LineInput.PackFactor (int?)`. */
  PackFactor: number;
  Quantity: number;
  CartonCapacity: number;
  UnitPrice: number;
  PortalItemKey: number;
  SeasonCode: string | null;
  LocCode: string | null;
  // Spec pack-analysis-constraint-solver-v1 — per-line dim override on the
  // AutoPack-shape. Verbatim passthrough onto LineInput by the BE adapter.
  // Legacy AutoPlan callers omit these → BE deserializes null → byte-equal
  // behavior. Used by the Bridge Tester operator-override workflow.
  /** Length in `DimensionUnit` — overrides item-master Length when set (solver only). */
  Length?: number | null;
  /** Width in `DimensionUnit` — overrides item-master Width when set (solver only). */
  Width?: number | null;
  /** Height in `DimensionUnit` — overrides item-master Height when set (solver only). */
  Height?: number | null;
  /** Weight in `WeightUnit` — overrides item-master NetWeight when set (solver only). */
  Weight?: number | null;
  /** Unit for length fields. Supported (case-insensitive): cm / mm / m / in / ft.
   *  Null / empty → "cm" (default). Unknown unit → bridge fails with -4913. */
  DimensionUnit?: string | null;
  /** Unit for the weight field. Supported (case-insensitive): kg / g / lb / oz.
   *  Null / empty → "kg" (default). */
  WeightUnit?: string | null;
}
